import { useState } from 'react';
import type { Product, CreateProductInput, ProductCategory, ProductUnit, TvaRate, Fournisseur, FournisseurAlternatif } from '@/types/product.types';
import type { ZoneStockage } from '@/types/inventory.types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Checkbox } from '@/components/ui/Checkbox';
import { BarcodeScanner } from '@/components/scanner/BarcodeScanner';
import { ProductLookupResultCard } from '@/components/scanner/ProductLookupResult';
import { openFoodFactsService, ProductLookupResult } from '@/services/openFoodFactsService';

interface ProductFormProps {
  product?: Product;
  onSubmit: (input: CreateProductInput) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const CATEGORIES: ProductCategory[] = [
  'Légumes',
  'Viandes',
  'Poissons',
  'Laitages',
  'Fruits',
  'Épices',
  'Condiments',
  // Catégories pour la réserve
  'Conserves',
  'Produits secs',
  'Huiles et vinaigres',
  'Pâtes et riz',
  'Farines et céréales',
  // Catégories pour les boissons
  'Boissons non alcoolisées',
  'Alcools',
  'Vins',
  'Bières',
  // Catégories pour l'entretien
  'Produits d\'entretien',
  'Produits d\'hygiène',
  'Emballages',
  'Matériel jetable',
  // Autres
  'Autres',
];

const UNITS: ProductUnit[] = ['kg', 'g', 'L', 'mL', 'unité'];

const TVA_RATES: TvaRate[] = [5.5, 10, 20];

export function ProductForm({ product, onSubmit, onCancel, isLoading }: ProductFormProps) {
  const [formData, setFormData] = useState<CreateProductInput>({
    nom: product?.nom || '',
    categorie: product?.categorie || 'Autres',
    unite: product?.unite || 'kg',
    code_barre: product?.code_barre || null,
    seuil_critique: product?.seuil_critique ?? 5,
    seuil_alerte: product?.seuil_alerte ?? 10,
    cout_unitaire: product?.cout_unitaire || null,
    fournisseur: product?.fournisseur || null,
    taux_tva: product?.taux_tva || 20,
    duree_conservation_jours: product?.duree_conservation_jours || null,
  });

  // États pour le scanner de codes-barres
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupResult, setLookupResult] = useState<ProductLookupResult | null>(null);

  // Gestion du scan de code-barres
  const handleBarcodeScan = async (barcode: string) => {
    setIsScannerOpen(false);
    setIsLookingUp(true);
    setLookupResult(null);

    try {
      const result = await openFoodFactsService.lookupBarcode(barcode);
      setLookupResult(result);

      // Si produit non trouvé, pré-remplir juste le code-barres
      if (!result.found) {
        setFormData(prev => ({ ...prev, code_barre: barcode }));
      }
    } catch (error) {
      console.error('Barcode lookup error:', error);
      setFormData(prev => ({ ...prev, code_barre: barcode }));
    } finally {
      setIsLookingUp(false);
    }
  };

  // Utiliser les données du produit scanné
  const handleUseScannedProduct = (scannedProduct: NonNullable<ProductLookupResult['product']>) => {
    // Mapper la catégorie Open Food Facts vers nos catégories
    const mappedCategory = mapToLocalCategory(scannedProduct.category);

    setFormData(prev => ({
      ...prev,
      nom: scannedProduct.name,
      code_barre: lookupResult?.barcode || prev.code_barre,
      categorie: mappedCategory,
    }));

    setLookupResult(null);
  };

  // Mapper les catégories Open Food Facts vers nos catégories locales
  const mapToLocalCategory = (offCategory: string): ProductCategory => {
    const categoryLower = offCategory.toLowerCase();

    if (categoryLower.includes('légume') || categoryLower.includes('vegetable')) return 'Légumes';
    if (categoryLower.includes('viande') || categoryLower.includes('meat')) return 'Viandes';
    if (categoryLower.includes('poisson') || categoryLower.includes('fish') || categoryLower.includes('seafood')) return 'Poissons';
    if (categoryLower.includes('lait') || categoryLower.includes('fromage') || categoryLower.includes('dairy') || categoryLower.includes('cheese')) return 'Laitages';
    if (categoryLower.includes('fruit')) return 'Fruits';
    if (categoryLower.includes('épice') || categoryLower.includes('spice')) return 'Épices';
    if (categoryLower.includes('sauce') || categoryLower.includes('condiment')) return 'Condiments';
    if (categoryLower.includes('conserve') || categoryLower.includes('canned')) return 'Conserves';
    if (categoryLower.includes('pâte') || categoryLower.includes('pasta')) return 'Pâtes et riz';
    if (categoryLower.includes('riz') || categoryLower.includes('rice')) return 'Pâtes et riz';
    if (categoryLower.includes('huile') || categoryLower.includes('oil') || categoryLower.includes('vinaigre')) return 'Huiles et vinaigres';
    if (categoryLower.includes('farine') || categoryLower.includes('flour') || categoryLower.includes('céréale')) return 'Farines et céréales';
    if (categoryLower.includes('boisson') || categoryLower.includes('beverage') || categoryLower.includes('soda') || categoryLower.includes('jus')) return 'Boissons non alcoolisées';
    if (categoryLower.includes('bière') || categoryLower.includes('beer')) return 'Bières';
    if (categoryLower.includes('vin') || categoryLower.includes('wine')) return 'Vins';
    if (categoryLower.includes('alcool') || categoryLower.includes('spirit') || categoryLower.includes('liquor')) return 'Alcools';

    return 'Autres';
  };

  // États pour les champs numériques (pour permettre des champs vides pendant l'édition)
  const [seuilCritiqueStr, setSeuilCritiqueStr] = useState(String(formData.seuil_critique));
  const [seuilAlerteStr, setSeuilAlerteStr] = useState(String(formData.seuil_alerte));

  const [showFournisseur, setShowFournisseur] = useState(!!product?.fournisseur);
  const [fournisseurData, setFournisseurData] = useState<Fournisseur>(
    product?.fournisseur || {
      nom: '',
      contact: '',
      email: '',
      telephone: '',
      reference: '',
    }
  );

  // Fournisseurs alternatifs
  const [showAlternatives, setShowAlternatives] = useState(
    !!(product?.fournisseurs_alternatifs && product.fournisseurs_alternatifs.length > 0)
  );
  const [alternatives, setAlternatives] = useState<FournisseurAlternatif[]>(
    product?.fournisseurs_alternatifs || []
  );

  const addAlternative = () => {
    setAlternatives([...alternatives, { nom: '', prix: 0, date: new Date().toISOString().split('T')[0] }]);
  };

  const updateAlternative = (index: number, field: keyof FournisseurAlternatif, value: string | number) => {
    const updated = [...alternatives];
    updated[index] = { ...updated[index], [field]: value };
    setAlternatives(updated);
  };

  const removeAlternative = (index: number) => {
    setAlternatives(alternatives.filter((_, i) => i !== index));
  };

  // Stock initial (seulement pour la création)
  const [addInitialStock, setAddInitialStock] = useState(false);
  const [initialStockZone, setInitialStockZone] = useState<ZoneStockage>('positive');
  const [initialStockQuantityStr, setInitialStockQuantityStr] = useState<string>('');
  const [initialStockDlc, setInitialStockDlc] = useState<string>('');
  const [initialStockNumeroLot, setInitialStockNumeroLot] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const stockQuantity = parseFloat(initialStockQuantityStr) || 0;

    // Filter valid alternatives
    const validAlternatives = showAlternatives
      ? alternatives.filter((a) => a.nom.trim() && a.prix > 0)
      : [];

    const submitData: CreateProductInput = {
      ...formData,
      fournisseur: showFournisseur && fournisseurData.nom ? fournisseurData : null,
      fournisseurs_alternatifs: validAlternatives.length > 0 ? validAlternatives : undefined,
      stock_initial: addInitialStock && stockQuantity > 0
        ? {
            zone: initialStockZone,
            quantite: stockQuantity,
            dlc: initialStockDlc || null,
            numero_lot: initialStockNumeroLot || null,
          }
        : undefined,
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Scanner de codes-barres */}
      <BarcodeScanner
        isOpen={isScannerOpen}
        onScan={handleBarcodeScan}
        onClose={() => setIsScannerOpen(false)}
      />

      {/* Résultat du scan */}
      {(isLookingUp || lookupResult) && (
        <div className="mb-4">
          <ProductLookupResultCard
            result={lookupResult}
            isLoading={isLookingUp}
            onUseProduct={handleUseScannedProduct}
            onManualEntry={() => setLookupResult(null)}
            onScanAgain={() => {
              setLookupResult(null);
              setIsScannerOpen(true);
            }}
          />
        </div>
      )}

      {/* Bouton scanner (seulement si pas de résultat en cours) */}
      {!product && !lookupResult && !isLookingUp && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Scanner un code-barres</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Remplissez automatiquement les informations du produit</p>
            </div>
            <Button
              type="button"
              onClick={() => setIsScannerOpen(true)}
              className="flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
              Scanner
            </Button>
          </div>
        </div>
      )}

      {/* Informations de base */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Informations de base</h3>

        <div>
          <Label htmlFor="nom">Nom du produit *</Label>
          <Input
            id="nom"
            type="text"
            value={formData.nom}
            onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
            required
            placeholder="Ex: Tomates cerises"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="categorie">Catégorie *</Label>
            <select
              id="categorie"
              value={formData.categorie}
              onChange={(e) => setFormData({ ...formData, categorie: e.target.value as ProductCategory })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="unite">Unité *</Label>
            <select
              id="unite"
              value={formData.unite}
              onChange={(e) => setFormData({ ...formData, unite: e.target.value as ProductUnit })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              {UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor="code_barre">Code-barre (optionnel)</Label>
          <div className="flex gap-2">
            <Input
              id="code_barre"
              type="text"
              value={formData.code_barre || ''}
              onChange={(e) => setFormData({ ...formData, code_barre: e.target.value || null })}
              placeholder="Ex: 3245678901234"
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsScannerOpen(true)}
              title="Scanner un code-barre"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
            </Button>
          </div>
        </div>
      </div>

      {/* Seuils de stock */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Seuils de stock</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="seuil_alerte">Seuil d'alerte *</Label>
            <Input
              id="seuil_alerte"
              type="number"
              step="0.01"
              value={seuilAlerteStr}
              onChange={(e) => {
                setSeuilAlerteStr(e.target.value);
                const val = parseFloat(e.target.value);
                setFormData({ ...formData, seuil_alerte: isNaN(val) ? 0 : val });
              }}
              required
              min="0"
              placeholder="10"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Quantité minimale avant alerte</p>
          </div>

          <div>
            <Label htmlFor="seuil_critique">Seuil critique *</Label>
            <Input
              id="seuil_critique"
              type="number"
              step="0.01"
              value={seuilCritiqueStr}
              onChange={(e) => {
                setSeuilCritiqueStr(e.target.value);
                const val = parseFloat(e.target.value);
                setFormData({ ...formData, seuil_critique: isNaN(val) ? 0 : val });
              }}
              required
              min="0"
              placeholder="5"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Quantité minimale avant rupture</p>
          </div>
        </div>

        <div>
          <Label htmlFor="duree_conservation">Durée de conservation (jours)</Label>
          <Input
            id="duree_conservation"
            type="number"
            value={formData.duree_conservation_jours || ''}
            onChange={(e) => {
              const val = e.target.value;
              setFormData({ ...formData, duree_conservation_jours: val ? parseInt(val) : null });
            }}
            min="1"
            placeholder="Ex: 7"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Durée typique avant péremption (aide au calcul DLC)</p>
        </div>
      </div>

      {/* Prix et TVA */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Prix et TVA</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cout_unitaire">Coût unitaire (€)</Label>
            <Input
              id="cout_unitaire"
              type="number"
              step="0.01"
              value={formData.cout_unitaire || ''}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({ ...formData, cout_unitaire: val ? parseFloat(val) : null });
              }}
              min="0"
              placeholder="0.00"
            />
          </div>

          <div>
            <Label htmlFor="taux_tva">Taux TVA (%)</Label>
            <select
              id="taux_tva"
              value={formData.taux_tva}
              onChange={(e) => setFormData({ ...formData, taux_tva: parseFloat(e.target.value) as TvaRate })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {TVA_RATES.map((rate) => (
                <option key={rate} value={rate}>
                  {rate}%
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Fournisseur */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Fournisseur</h3>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowFournisseur(!showFournisseur)}
          >
            {showFournisseur ? 'Masquer' : 'Ajouter un fournisseur'}
          </Button>
        </div>

        {showFournisseur && (
          <div className="space-y-3 pl-4 border-l-2 border-gray-200">
            <div>
              <Label htmlFor="fournisseur_nom">Nom du fournisseur</Label>
              <Input
                id="fournisseur_nom"
                type="text"
                value={fournisseurData.nom}
                onChange={(e) => setFournisseurData({ ...fournisseurData, nom: e.target.value })}
                placeholder="Ex: Maraîcher Local"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="fournisseur_contact">Contact</Label>
                <Input
                  id="fournisseur_contact"
                  type="text"
                  value={fournisseurData.contact || ''}
                  onChange={(e) => setFournisseurData({ ...fournisseurData, contact: e.target.value })}
                  placeholder="Jean Dupont"
                />
              </div>

              <div>
                <Label htmlFor="fournisseur_telephone">Téléphone</Label>
                <Input
                  id="fournisseur_telephone"
                  type="tel"
                  value={fournisseurData.telephone || ''}
                  onChange={(e) => setFournisseurData({ ...fournisseurData, telephone: e.target.value })}
                  placeholder="01 23 45 67 89"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="fournisseur_email">Email</Label>
              <Input
                id="fournisseur_email"
                type="email"
                value={fournisseurData.email || ''}
                onChange={(e) => setFournisseurData({ ...fournisseurData, email: e.target.value })}
                placeholder="contact@fournisseur.fr"
              />
            </div>

            <div>
              <Label htmlFor="fournisseur_reference">Référence produit</Label>
              <Input
                id="fournisseur_reference"
                type="text"
                value={fournisseurData.reference || ''}
                onChange={(e) => setFournisseurData({ ...fournisseurData, reference: e.target.value })}
                placeholder="REF-123456"
              />
            </div>
          </div>
        )}
      </div>

      {/* Fournisseurs alternatifs */}
      {showFournisseur && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Fournisseurs alternatifs</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAlternatives(!showAlternatives);
                if (!showAlternatives && alternatives.length === 0) addAlternative();
              }}
            >
              {showAlternatives ? 'Masquer' : 'Comparer les prix'}
            </Button>
          </div>

          {showAlternatives && (
            <div className="space-y-3 pl-4 border-l-2 border-blue-200">
              {alternatives.map((alt, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Fournisseur alternatif {index + 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAlternative(index)}
                      className="text-red-500 hover:text-red-700 text-xs h-6 px-2"
                    >
                      Supprimer
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor={`alt_nom_${index}`}>Nom</Label>
                      <Input
                        id={`alt_nom_${index}`}
                        type="text"
                        value={alt.nom}
                        onChange={(e) => updateAlternative(index, 'nom', e.target.value)}
                        placeholder="Nom du fournisseur"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`alt_prix_${index}`}>Prix (€)</Label>
                      <Input
                        id={`alt_prix_${index}`}
                        type="number"
                        step="0.01"
                        min="0"
                        value={alt.prix || ''}
                        onChange={(e) => updateAlternative(index, 'prix', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`alt_ref_${index}`}>Référence (optionnel)</Label>
                    <Input
                      id={`alt_ref_${index}`}
                      type="text"
                      value={alt.reference || ''}
                      onChange={(e) => updateAlternative(index, 'reference', e.target.value)}
                      placeholder="REF-XXX"
                    />
                  </div>
                </div>
              ))}

              <Button type="button" variant="outline" size="sm" onClick={addAlternative} className="w-full">
                + Ajouter un fournisseur alternatif
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Stock initial (seulement pour création) */}
      {!product && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Stock initial</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Ajouter une quantité de stock lors de la création</p>
            </div>
            <Checkbox
              checked={addInitialStock}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddInitialStock(e.target.checked)}
              label="Ajouter du stock"
            />
          </div>

          {addInitialStock && (
            <div className="space-y-3 pl-4 border-l-2 border-primary">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="stock_zone">Zone de stockage *</Label>
                  <select
                    id="stock_zone"
                    value={initialStockZone}
                    onChange={(e) => setInitialStockZone(e.target.value as ZoneStockage)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="positive">Zone Positive (Tempérée)</option>
                    <option value="negative">Zone Négative (Surgelés)</option>
                    <option value="frigo">Frigo (0-4°C)</option>
                    <option value="reserve">Réserve (Produits secs)</option>
                    <option value="cave">Cave (Boissons et alcools)</option>
                    <option value="entretien">Entretien et office</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="stock_quantity">Quantité initiale *</Label>
                  <Input
                    id="stock_quantity"
                    type="number"
                    step="0.01"
                    value={initialStockQuantityStr}
                    onChange={(e) => setInitialStockQuantityStr(e.target.value)}
                    min="0"
                    placeholder="Quantité"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">En {formData.unite}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="stock_dlc">Date limite de consommation (DLC)</Label>
                  <Input
                    id="stock_dlc"
                    type="date"
                    value={initialStockDlc}
                    onChange={(e) => setInitialStockDlc(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Optionnel - pour la traçabilité</p>
                </div>

                <div>
                  <Label htmlFor="stock_numero_lot">Numéro de lot</Label>
                  <Input
                    id="stock_numero_lot"
                    type="text"
                    value={initialStockNumeroLot}
                    onChange={(e) => setInitialStockNumeroLot(e.target.value)}
                    placeholder="Ex: LOT-2024-001"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Optionnel - référence fournisseur</p>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <span className="font-medium">Astuce:</span> Si vous renseignez une DLC, un lot sera automatiquement créé pour ce stock.
                  Vous pourrez gérer vos lots depuis la page "Lots & DLC".
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Boutons d'action */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Annuler
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Enregistrement...' : product ? 'Mettre à jour' : 'Créer le produit'}
        </Button>
      </div>
    </form>
  );
}
